// A6 Quality Gate (stage pelaksanaan_pemilihan): the last check before the
// offer is uploaded.
//   counts     pemeriksaan selesai, persyaratan sesuai, gap, belum diperiksa
//              (the last three from the Reviu Persyaratan matrix)
//   checklist  ten checks, each saved as it is ticked
//   telaah     the offer text, scanned by keyword for the main parts and
//              leftover template markers (indications, never a verdict)
//   report     Unduh laporan Word
import { useEffect, useId, useState } from "react";
import * as API from "../../api";
import { Field, Notice, SkeletonRows, useAsync } from "../../ui/components";
import { Glyph } from "../../ui/icons";
import * as Work from "./api";
import type { Quality } from "./api";
import { CountTiles, WorkSection } from "./shared";

export function QualityGate({ projectId }: { projectId: string }) {
  const uid = useId();
  const quality = useAsync<Quality>(() => Work.getQuality(projectId), [projectId]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (quality.data) setText(quality.data.offerText);
  }, [quality.data]);

  const save = async (input: Work.QualitySave, what: string) => {
    setBusy(what);
    setError(null);
    try {
      quality.set(await Work.saveQuality(projectId, input));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  let body;
  const q = quality.data;
  if (quality.loading && !q) body = <SkeletonRows count={4} label="Memuat quality gate" />;
  else if (quality.error && !q) {
    body = (
      <Notice
        tone="danger"
        title="Quality gate belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={quality.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(quality.error)}</p>
      </Notice>
    );
  } else if (q) {
    const r = q.requirements;
    body = (
      <>
        <CountTiles
          label="Ringkasan quality gate"
          tiles={[
            { label: "Pemeriksaan selesai", value: `${q.done}/${q.checklist.length}` },
            { label: "Persyaratan sesuai", value: r.sesuai, note: "Dari Reviu Persyaratan" },
            { label: "Gap tercatat", value: r.belumSesuai, note: "Status Belum sesuai" },
            { label: "Belum diperiksa", value: r.belumDiperiksa },
          ]}
        />
        {r.belumSesuai > 0 ? (
          <Notice tone="warning" title={`${r.belumSesuai} persyaratan masih Belum sesuai`}>
            <p>Selesaikan gap tersebut sebelum penawaran diunggah.</p>
          </Notice>
        ) : null}

        <div className="wk-card">
          <h3 className="wk-subtitle">Checklist pemeriksaan akhir</h3>
          <p className="wk-note">Centang hanya setelah buktinya Anda periksa sendiri. Daftar ini tidak menggantikan ketentuan khusus dokumen pemilihan.</p>
          <ul className="pk-checklist" aria-label="Checklist pemeriksaan akhir">
            {q.checklist.map((c, i) => (
              <li key={c.key} className="pk-item" data-done={c.done ? "" : undefined}>
                <label className="pk-check" htmlFor={`${uid}-${c.key}`}>
                  <input id={`${uid}-${c.key}`} type="checkbox" checked={c.done} disabled={busy !== null} onChange={(e) => void save({ checks: { [c.key]: e.currentTarget.checked } }, c.key)} />
                  <span className="wk-check-label">
                    <span className="num">{String(i + 1).padStart(2, "0")}.</span> {c.label}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div className="wk-card">
          <h3 className="wk-subtitle">Telaah teks penawaran</h3>
          <Field id={`${uid}-offer`} label="Teks inti penawaran" hint="Tempel teks surat penawaran, metode pelaksanaan, daftar lampiran, dan rincian harga. Telaah ini mencari indikasi dari kata kunci, bukan memeriksa isi lampiran." wide>
            <textarea id={`${uid}-offer`} rows={6} maxLength={60_000} value={text} onChange={(e) => setText(e.currentTarget.value)} />
          </Field>
          <div className="wk-actions">
            <button type="button" className="btn btn-primary" disabled={busy !== null || text === q.offerText} onClick={() => void save({ offerText: text }, "offer")}>
              {busy === "offer" ? "Menelaah..." : "Telaah dan simpan"}
            </button>
          </div>
          {q.issues.length > 0 ? (
            <ul className="wk-issues" aria-label="Hasil telaah teks">
              {q.issues.map((i) => (
                <li key={i.key} className="wk-issue" data-ok={i.ok ? "" : undefined}>
                  <Glyph name={i.ok ? "tickCircle" : "warning"} size={18} />
                  <span>
                    <strong>{i.name}</strong>
                    <span className="wk-issue-text">{i.message}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="wk-empty">Belum ada teks yang ditelaah.</p>
          )}
        </div>

        <div className="wk-actions">
          <a className="btn btn-secondary" href={Work.qualityReportUrl(projectId)} download>
            <Glyph name="documentDownload" size={18} />
            <span>Unduh laporan Word</span>
          </a>
          <span className="wk-hint">Pluto tidak mengirim penawaran ke SPSE dan tidak menyatakan penawaran lulus evaluasi.</span>
        </div>
      </>
    );
  }

  return (
    <WorkSection id="quality-gate" title="Quality Gate" lead="Pemeriksaan terakhir sebelum penawaran diunggah: kepatuhan, lampiran, harga, jadwal, dan persetujuan internal.">
      {body}
      {error ? (
        <Notice tone="danger" title="Belum tersimpan">
          <p>{error}</p>
        </Notice>
      ) : null}
    </WorkSection>
  );
}
